import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { ChangeOrder, ChangeOrderStatus, IdentifiableObject } from '../models'
import { axiosInstance } from '../helpers'
import { toast } from 'react-toastify'

export const useChangeOrder = () => {
    const [changeOrderList, setChangeOrderList] = useState<ChangeOrder[]>([])
    const [isLoading, setIsLoading] = useState(false)

    const [
        changeOrderFilterState,
        setChangeOrderFilterState,
    ] = useState<ChangeOrderFilterState>({
        status: ChangeOrderStatus.PENDING,
        unitSearch: '',
    })

    const getChangeOrderList = async (params?: {
        lower_bound_date?: string
        schedule_id?: number
        workorder?: number
    }) => {
        try {
            setIsLoading(true)
            const response = await axiosInstance.get(
                'workorder/simple-change-order/',
                { params: params },
            )
            const updatedChangeOrderList: ChangeOrder[] = response.data
            setChangeOrderList(updatedChangeOrderList)
            setIsLoading(false)
            return updatedChangeOrderList
        } catch (error) {
            setIsLoading(false)
            toast.error('Error getting change orders')
        }
    }

    const approveDenyChangeOrder = async (
        changeOrder: ChangeOrder,
        status: ChangeOrderStatus,
    ) => {
        try {
            const response = await axiosInstance.post(
                `workorder/change-order/${changeOrder.id}/change_status/`,
                {
                    status: status,
                },
            )

            const returnedChangeOrder: ChangeOrder = response.data.change_order

            updateChangeOrderState({
                ...changeOrder,
                status: returnedChangeOrder.status,
                handled_by: returnedChangeOrder.handled_by,
                handled_at: returnedChangeOrder.handled_at,
                created_by:
                    returnedChangeOrder.created_by ?? changeOrder.created_by,
            })

            toast.success('Change Order updated successfully')
        } catch (error) {
            toast.error('Error updating Change Order')
        }
    }

    const deleteChangeOrder = async (changeOrder: ChangeOrder) => {
        try {
            await axiosInstance.delete(
                `workorder/change-order/${changeOrder.id}/`,
            )
            _removeIdentifiable(changeOrder, setChangeOrderList)
            toast.success('Change Order deleted successfully!')
        } catch (error) {
            setIsLoading(false)
            toast.error(getChangeOrderDeleteError(error))
            throw error
        }
    }

    const updateChangeOrderState = (changeOrder: ChangeOrder) => {
        _insertOrUpdateIdentifiable(changeOrder, setChangeOrderList)
    }

    const pendingChangeOrderCount = useMemo(() => {
        return changeOrderList.filter(
            (co) => co.status === ChangeOrderStatus.PENDING,
        ).length
    }, [changeOrderList])

    const _insertOrUpdateIdentifiable = <T extends IdentifiableObject>(
        updatedValue: T,
        dispatch: Dispatch<SetStateAction<T[]>>,
    ) => {
        dispatch((old) => {
            const safeOldValue = old ? [...old] : []

            let found = false
            for (let i = 0; i < safeOldValue.length; i++) {
                const current = safeOldValue[i]
                if (current.id === updatedValue.id) {
                    safeOldValue[i] = updatedValue
                    found = true
                    break
                }
            }

            if (!found) {
                safeOldValue.push(updatedValue)
            }

            return safeOldValue
        })
    }

    const _removeIdentifiable = <T extends IdentifiableObject>(
        deleteValue: T,
        dispatch: Dispatch<SetStateAction<T[]>>,
    ) => {
        dispatch((old) => {
            const safeOldValue = old ? [...old] : []

            return safeOldValue.filter((v) => v.id !== deleteValue.id)
        })
    }

    const filteredChangeOrders = useMemo(() => {
        const filters = changeOrderFilterState
        return changeOrderList.filter((co) => {
            const unitSearchValid =
                co.location?.unit_name
                    ?.toLocaleLowerCase()
                    .startsWith(filters.unitSearch) || filters.unitSearch === ''

            const statusValid = co.status === filters.status

            return unitSearchValid && statusValid
        })
    }, [changeOrderFilterState, changeOrderList])

    return {
        changeOrderList,
        filteredChangeOrders,
        isLoading,
        changeOrderFilterState,
        pendingChangeOrderCount,
        getChangeOrderList,
        approveDenyChangeOrder,
        setChangeOrderFilterState,
        deleteChangeOrder,
    }
}

export interface ChangeOrderFilterState {
    status: ChangeOrderStatus
    unitSearch: string
}

export const changeOrderStatusOptions = [
    ChangeOrderStatus.PENDING,
    ChangeOrderStatus.APPROVED,
    ChangeOrderStatus.DENIED,
] as const

const getChangeOrderDeleteError = (error: unknown) => {
    const data = (error as {
        response?: { data?: { key?: string; message?: string } }
    })?.response?.data
    if (data?.key === 'CHANGE_ORDER_LOCKED') {
        return 'Cannot delete: work order is approved or invoiced'
    }
    return data?.message ?? 'Error deleting change order'
}

export interface ChangeOrderController {
    changeOrderList: ChangeOrder[]
    filteredChangeOrders: ChangeOrder[]
    isLoading: boolean
    changeOrderFilterState: ChangeOrderFilterState
    pendingChangeOrderCount: number
    getChangeOrderList: (params?: {
        lower_bound_date?: string
        schedule_id?: number
        workorder?: number
    }) => Promise<ChangeOrder[] | undefined>
    approveDenyChangeOrder: (
        changeOrder: ChangeOrder,
        status: ChangeOrderStatus,
    ) => Promise<void>
    setChangeOrderFilterState: Dispatch<SetStateAction<ChangeOrderFilterState>>
    deleteChangeOrder: (changeOrder: ChangeOrder) => Promise<void>
}
